import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import './CpdLandingPage.css';
import { API_URL } from '../constants';

const CHROME_EXT_URL = 'https://chromewebstore.google.com/detail/cpd-pro-%E2%80%93-article-quiz/jahjkbeffflpapclognafdfjpcphcfej';

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Reveal({ children, className = '', as: Tag = 'div', ...rest }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (reducedMotion()) {
      setOn(true);
      return undefined;
    }
    const showIfVisible = () => {
      const rect = node.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92) setOn(true);
    };
    showIfVisible();
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setOn(true);
      },
      { threshold: 0.05, rootMargin: '0px 0px -4% 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref} className={`${className} lp-reveal${on ? ' is-in' : ''}`} {...rest}>
      {children}
    </Tag>
  );
}

function CountUp({ value, play }) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!play) return undefined;
    if (reducedMotion()) {
      setN(value);
      return undefined;
    }
    const start = performance.now();
    const duration = 1100;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.round(value * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [play, value]);

  return <>{n}</>;
}

function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function ringSlice(cx, cy, r0, r1, start, end) {
  const [x0, y0] = polar(cx, cy, r1, start);
  const [x1, y1] = polar(cx, cy, r1, end);
  const [x2, y2] = polar(cx, cy, r0, end);
  const [x3, y3] = polar(cx, cy, r0, start);
  const large = end - start > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${r1} ${r1} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r0} ${r0} 0 ${large} 0 ${x3} ${y3} Z`;
}

const YEAR_EARN = [2, 0, 0, 2, 1, 2, 0, 0, 2, 2, 2, 1];
const MONTH_ALLOWANCE = 2;

function yearTotals(upTo) {
  let bank = 0;
  let earned = 0;
  for (let i = 0; i <= upTo; i += 1) {
    bank += MONTH_ALLOWANCE;
    const take = Math.min(YEAR_EARN[i], bank);
    bank -= take;
    earned += take;
  }
  return { bank, earned, granted: MONTH_ALLOWANCE * (upTo + 1) };
}

function HeroStage({ t }) {
  const stageRef = useRef(null);
  const certHover = useRef(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [pick, setPick] = useState(null);
  const [flipped, setFlipped] = useState(false);

  const onMove = (event) => {
    if (reducedMotion()) return;
    const box = stageRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = (event.clientX - box.left) / box.width;
    const py = (event.clientY - box.top) / box.height;
    setTilt({ x: (px - 0.5) * 10, y: (py - 0.5) * -8 });
  };

  const onLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (reducedMotion()) return undefined;
    const id = setInterval(() => {
      if (!certHover.current) setFlipped((v) => !v);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  const correct = pick === 'b';

  return (
    <div
      className="hero-stage"
      ref={stageRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <div
        className="hero-stage-inner"
        style={{ transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)` }}
      >
        <button
          type="button"
          className={`cert-flip${flipped ? ' is-flipped' : ''}${correct ? ' is-earned' : ''}`}
          onClick={() => setFlipped((v) => !v)}
          onMouseEnter={() => { certHover.current = true; }}
          onMouseLeave={() => { certHover.current = false; }}
          aria-pressed={flipped}
          aria-label={t('cpdLanding.hero.certFlipHint')}
        >
          <div className="cert-card cert-front">
            <div className="cert-seal" aria-hidden="true">
              <span>✓</span>
            </div>
            <p className="cert-kicker">{t('cpdLanding.hero.certKicker')}</p>
            <h3 className="cert-title">{t('cpdLanding.hero.certTitle')}</h3>
            <p className="cert-meta">{t('cpdLanding.hero.certMeta')}</p>
            <div className="cert-row">
              <span>{t('cpdLanding.hero.certPoints')}</span>
              <strong>1.0 CPD</strong>
            </div>
            <div className="cert-row">
              <span>ID</span>
              <strong className="cert-id">CPD-8F2A-79C4</strong>
            </div>
            <div className={`cert-bar${correct ? ' is-filled' : ''}`} aria-hidden="true" />
            <p className="cert-flip-hint">{t('cpdLanding.hero.certFlipHint')}</p>
          </div>
          <div className="cert-card cert-back">
            <p className="cert-kicker">{t('cpdLanding.hero.certBackKicker')}</p>
            <h3 className="cert-title">{t('cpdLanding.hero.certBackTitle')}</h3>
            <p className="cert-meta">{t('cpdLanding.hero.certBackMeta')}</p>
            <div className="cert-row">
              <span>ID</span>
              <strong className="cert-id">CPD-8F2A-79C4</strong>
            </div>
            <div className="cert-row">
              <span>NiRV</span>
              <strong>{t('cpdLanding.hero.certBackStatus')}</strong>
            </div>
            <div className="cert-verify" aria-hidden="true">
              <span /><span /><span /><span />
            </div>
          </div>
        </button>

        <div className="quiz-float">
          <p className="quiz-float-label">{t('cpdLanding.hero.quizLabel')}</p>
          <p className="quiz-float-q">{t('cpdLanding.hero.quizQuestion')}</p>
          <div className="quiz-options">
            <button
              type="button"
              className={`quiz-opt${pick === 'a' ? ' is-wrong' : ''}`}
              onClick={() => setPick('a')}
            >
              {t('cpdLanding.hero.quizA')}
            </button>
            <button
              type="button"
              className={`quiz-opt${pick === 'b' ? ' is-on' : ''}`}
              onClick={() => setPick('b')}
            >
              {t('cpdLanding.hero.quizB')}
            </button>
          </div>
          {pick === 'a' && <p className="quiz-feedback">{t('cpdLanding.hero.quizWrong')}</p>}
          {pick === 'b' && <p className="quiz-feedback is-ok">{t('cpdLanding.hero.quizCorrect')}</p>}
        </div>

        <div className="ext-float">
          <span className="ext-dot" />
          {t('cpdLanding.hero.extChip')}
        </div>
      </div>
    </div>
  );
}

function YearCycle() {
  const { t, i18n } = useTranslation();
  const wrapRef = useRef(null);
  const hovered = useRef(false);
  const [month, setMonth] = useState(reducedMotion() ? 11 : 0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || paused || reducedMotion()) return undefined;
    const id = setInterval(() => {
      if (!hovered.current) setMonth((m) => (m + 1) % 12);
    }, 1100);
    return () => clearInterval(id);
  }, [inView, paused]);

  const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-GB';
  const names = YEAR_EARN.map((_, i) =>
    new Date(2026, i, 1).toLocaleString(locale, { month: 'short' })
  );
  const totals = yearTotals(month);
  const cx = 120;
  const cy = 120;

  const selectMonth = (i) => {
    setMonth(i);
    setPaused(true);
  };

  return (
    <div
      className="year-cycle"
      ref={wrapRef}
      onMouseEnter={() => { hovered.current = true; }}
      onMouseLeave={() => { hovered.current = false; }}
    >
      <div className="cycle-ring-wrap">
        <svg className="cycle-svg" viewBox="0 0 240 240" role="img" aria-label={t('cpdLanding.yearCycle.title')}>
          <circle cx="120" cy="120" r="56" fill="#08111c" stroke="rgba(196,163,90,.4)" strokeWidth="1.5" />
          <circle
            cx="120"
            cy="120"
            r="56"
            fill="none"
            stroke="rgba(196,163,90,.18)"
            strokeWidth="6"
          />
          <circle
            cx="120"
            cy="120"
            r="56"
            fill="none"
            stroke="#c4a35a"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${(totals.granted / 24) * 2 * Math.PI * 56} ${2 * Math.PI * 56}`}
            transform="rotate(-90 120 120)"
          />
          {YEAR_EARN.map((earn, i) => {
            const gap = 3.2;
            const start = i * 30 + gap;
            const end = (i + 1) * 30 - gap;
            const isPast = i < month;
            const isNow = i === month;
            let fill = '#1e324c';
            if (isNow) fill = '#f8f4ea';
            else if (isPast && earn > 0) fill = '#c4a35a';
            else if (isPast) fill = 'rgba(196,163,90,.32)';
            const [lx, ly] = polar(cx, cy, 112, i * 30 + 15);
            return (
              <g key={names[i]}>
                <path
                  d={ringSlice(cx, cy, 64, 94, start, end)}
                  fill={fill}
                  className="cycle-slice"
                  onClick={() => selectMonth(i)}
                />
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className={`cycle-label${isNow ? ' is-on' : ''}`}
                >
                  {names[i]}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="cycle-center">
          <strong>{totals.bank.toFixed(1)}</strong>
          <span>{t('cpdLanding.yearCycle.banked')}</span>
        </div>
      </div>
      <div className="cycle-side">
        <div className="cycle-metrics">
          <div>
            <strong>{totals.granted.toFixed(1)}</strong>
            <span>{t('cpdLanding.yearCycle.granted')}</span>
          </div>
          <div>
            <strong>{totals.earned.toFixed(1)}</strong>
            <span>{t('cpdLanding.yearCycle.earned')}</span>
          </div>
          <div>
            <strong>{totals.bank.toFixed(1)}</strong>
            <span>{t('cpdLanding.yearCycle.rollover')}</span>
          </div>
        </div>
        <div className="cycle-ledger">
          {YEAR_EARN.map((earn, i) => (
            <button
              key={names[i]}
              type="button"
              className={`cycle-row${i === month ? ' is-on' : ''}${i > month ? ' is-future' : ''}`}
              onClick={() => selectMonth(i)}
            >
              <span>{names[i]}</span>
              <span className="cycle-bar" aria-hidden="true">
                <i style={{ width: `${(earn / MONTH_ALLOWANCE) * 100}%` }} />
              </span>
              <em>{earn.toFixed(1)}</em>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="cycle-play"
          onClick={() => setPaused((v) => !v)}
        >
          {paused ? t('cpdLanding.yearCycle.play') : t('cpdLanding.yearCycle.pause')}
        </button>
      </div>
    </div>
  );
}

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setSeen(true); },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, seen];
}

const ANNUAL_PRICE = 199.99;

function CostCalculator({ onRegister }) {
  const { t, i18n } = useTranslation();
  const [points, setPoints] = useState(20);
  const [rate, setRate] = useState(125);

  const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-GB';
  const money = (value, digits = 0) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);

  const seminar = points * rate;
  const saving = seminar - ANNUAL_PRICE;
  const perPoint = ANNUAL_PRICE / points;
  const max = Math.max(seminar, ANNUAL_PRICE);

  return (
    <div className="calc">
      <div className="calc-controls">
        <label className="calc-field">
          <span>{t('cpdLanding.calc.pointsLabel')}</span>
          <strong>{points}</strong>
          <input
            type="range"
            min="4"
            max="40"
            step="1"
            value={points}
            onChange={(event) => setPoints(Number(event.target.value))}
          />
        </label>
        <label className="calc-field">
          <span>{t('cpdLanding.calc.rateLabel')}</span>
          <strong>{money(rate)}</strong>
          <input
            type="range"
            min="25"
            max="300"
            step="5"
            value={rate}
            onChange={(event) => setRate(Number(event.target.value))}
          />
        </label>
        <p className="calc-note">{t('cpdLanding.calc.assumption')}</p>
      </div>

      <div className="calc-out">
        <div className="calc-duel">
          <div className="calc-bar-row">
            <span className="calc-bar-label">{t('cpdLanding.calc.traditional')}</span>
            <span className="calc-bar" aria-hidden="true">
              <i className="is-trad" style={{ width: `${(seminar / max) * 100}%` }} />
            </span>
            <em>{money(seminar)}</em>
          </div>
          <div className="calc-bar-row">
            <span className="calc-bar-label">CPD Pro</span>
            <span className="calc-bar" aria-hidden="true">
              <i className="is-pro" style={{ width: `${(ANNUAL_PRICE / max) * 100}%` }} />
            </span>
            <em>{money(ANNUAL_PRICE)}</em>
          </div>
        </div>

        <div className="calc-metrics">
          <div className="calc-metric is-lead">
            <strong>{money(Math.max(0, saving))}</strong>
            <span>{t('cpdLanding.calc.saving')}</span>
          </div>
          <div className="calc-metric">
            <strong>{money(perPoint, 2)}</strong>
            <span>{t('cpdLanding.calc.perPoint')}</span>
          </div>
          <div className="calc-metric">
            <strong>{money(rate)}</strong>
            <span>{t('cpdLanding.calc.perPointTrad')}</span>
          </div>
        </div>

        <button type="button" className="calc-cta" onClick={onRegister}>
          {t('cpdLanding.calc.cta')}
        </button>
      </div>
    </div>
  );
}

const COMPARE_EFFORT = [[92, 18], [88, 22], [80, 12], [72, 10]];

function CompareDuel() {
  const { t } = useTranslation();
  const [ref, seen] = useInView(0.25);
  const [focus, setFocus] = useState(0);

  return (
    <div className="duel" ref={ref}>
      <div className="duel-head">
        <span>{t('cpdLanding.whyPage.compareLeft')}</span>
        <span>{t('cpdLanding.whyPage.compareRight')}</span>
      </div>
      {COMPARE_EFFORT.map(([trad, pro], i) => (
        <button
          key={t(`cpdLanding.whyPage.compareRow${i + 1}Left`)}
          type="button"
          className={`duel-row${focus === i ? ' is-on' : ''}`}
          onClick={() => setFocus(i)}
          onMouseEnter={() => setFocus(i)}
        >
          <span className="duel-side is-trad">
            <span className="duel-text">{t(`cpdLanding.whyPage.compareRow${i + 1}Left`)}</span>
            <span className="duel-track">
              <i style={{ width: seen ? `${trad}%` : 0, transitionDelay: `${i * 90}ms` }} />
            </span>
          </span>
          <span className="duel-side is-pro">
            <span className="duel-text">{t(`cpdLanding.whyPage.compareRow${i + 1}Right`)}</span>
            <span className="duel-track">
              <i style={{ width: seen ? `${pro}%` : 0, transitionDelay: `${i * 90}ms` }} />
            </span>
          </span>
        </button>
      ))}
      <p className="duel-caption">{t('cpdLanding.whyPage.compareCaption')}</p>
    </div>
  );
}

const DEMO_CERT_ID = 'CPD-8F2A-79C4';

function CertificateVerifier() {
  const { t } = useTranslation();
  const [value, setValue] = useState(DEMO_CERT_ID);
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => clearTimers, []);

  const run = () => {
    clearTimers();
    setResult(null);
    setStage(0);
    const match = value.trim().toUpperCase() === DEMO_CERT_ID;
    const beat = reducedMotion() ? 0 : 340;
    [1, 2, 3, 4].forEach((step, i) => {
      timers.current.push(setTimeout(() => setStage(step), beat * (i + 1)));
    });
    timers.current.push(setTimeout(() => setResult(match ? 'ok' : 'unknown'), beat * 5));
  };

  const running = stage > 0 && !result;

  return (
    <div className={`verify${result ? ` is-${result}` : ''}${running ? ' is-running' : ''}`}>
      <div className="verify-form">
        <label className="verify-label" htmlFor="verify-id">
          {t('cpdLanding.verify.inputLabel')}
        </label>
        <div className="verify-row">
          <input
            id="verify-id"
            className="verify-input"
            value={value}
            spellCheck="false"
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') run(); }}
          />
          <button type="button" className="verify-btn" onClick={run}>
            {t('cpdLanding.verify.button')}
          </button>
        </div>
        <p className="verify-note">{t('cpdLanding.verify.note')}</p>
      </div>

      <div className="verify-panel">
        <div className="verify-scan" aria-hidden="true">
          <span style={{ width: `${(Math.min(stage, 4) / 4) * 100}%` }} />
        </div>
        <ol className="verify-steps">
          {[1, 2, 3, 4].map((step) => (
            <li key={step} className={stage >= step ? 'is-done' : ''}>
              <span className="verify-tick" aria-hidden="true">✓</span>
              {t(`cpdLanding.verify.step${step}`)}
            </li>
          ))}
        </ol>
        <div className="verify-result" role="status">
          {result === 'ok' && (
            <>
              <strong>{t('cpdLanding.verify.ok')}</strong>
              <span>{t('cpdLanding.verify.okSub')}</span>
            </>
          )}
          {result === 'unknown' && (
            <>
              <strong>{t('cpdLanding.verify.unknown')}</strong>
              <span>{t('cpdLanding.verify.unknownSub')}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function HowInteractive({ t, navigate }) {
  const [step, setStep] = useState(0);
  const wrapRef = useRef(null);
  const hovered = useRef(false);

  useEffect(() => {
    if (reducedMotion()) return undefined;
    const tick = () => {
      if (!hovered.current) setStep((s) => (s + 1) % 4);
    };
    const id = setInterval(tick, 3800);
    return () => clearInterval(id);
  }, []);

  const steps = [1, 2, 3, 4].map((n) => ({
    n,
    title: t(`cpdLanding.howWorks.step${n}Title`),
    desc: t(`cpdLanding.howWorks.step${n}Desc`),
  }));

  return (
    <div
      className="how-interactive"
      ref={wrapRef}
      onMouseEnter={() => { hovered.current = true; }}
      onMouseLeave={() => { hovered.current = false; }}
    >
      <div className="how-rail" role="tablist" aria-label={t('cpdLanding.howWorks.title')}>
        {steps.map((item, idx) => (
          <button
            key={item.n}
            type="button"
            role="tab"
            aria-selected={step === idx}
            className={`how-rail-btn${step === idx ? ' is-on' : ''}`}
            onClick={() => setStep(idx)}
          >
            <span className="how-rail-num">{item.n}</span>
            <span className="how-rail-title">{item.title}</span>
          </button>
        ))}
      </div>
      <div className="how-panel" role="tabpanel">
        <p className="how-panel-kicker">{t('cpdLanding.howWorks.intro')}</p>
        <div className={`how-scene is-step-${step}`} aria-hidden="true">
          {step === 0 && (
            <div className="scene-lib">
              <article className="scene-card is-on"><b /><i /><i className="short" /></article>
              <article className="scene-card"><b /><i /><i className="short" /></article>
              <article className="scene-card"><b /><i /><i className="short" /></article>
            </div>
          )}
          {step === 1 && (
            <div className="scene-quiz">
              <p />
              <span /><span className="on" /><span />
            </div>
          )}
          {step === 2 && (
            <div className="scene-cert">
              <em>✓</em>
              <strong>1.0 CPD</strong>
              <i>CPD-8F2A-79C4</i>
            </div>
          )}
          {step === 3 && (
            <div className="scene-log">
              <span style={{ height: '42%' }} />
              <span style={{ height: '68%' }} />
              <span className="on" style={{ height: '92%' }} />
              <span style={{ height: '55%' }} />
            </div>
          )}
        </div>
        <h3>{steps[step].title}</h3>
        <p>{steps[step].desc}</p>
        <button className="link-btn" type="button" onClick={() => navigate('/hoe-werkt-cpd-pro')}>
          {t('cpdLanding.howWorks.link')}
        </button>
      </div>
    </div>
  );
}

function ExtensionDemo({ t }) {
  const [step, setStep] = useState(0);
  const hovered = useRef(false);

  useEffect(() => {
    if (reducedMotion()) return undefined;
    const id = setInterval(() => {
      if (!hovered.current) setStep((s) => (s + 1) % 4);
    }, 4200);
    return () => clearInterval(id);
  }, []);

  const panels = [
    {
      title: t('cpdLanding.extDownload.step1Title'),
      body: t('cpdLanding.extDownload.step1Desc'),
      mock: 'store',
    },
    {
      title: t('cpdLanding.extDownload.step2Title'),
      body: t('cpdLanding.extDownload.step2Desc'),
      mock: 'panel',
    },
    {
      title: t('cpdLanding.extDownload.step3Title'),
      body: t('cpdLanding.extDownload.step3Desc'),
      mock: 'extract',
    },
    {
      title: t('cpdLanding.extDownload.step4Title'),
      body: t('cpdLanding.extDownload.step4Desc'),
      mock: 'earn',
    },
  ];

  return (
    <div
      className="ext-demo"
      onMouseEnter={() => { hovered.current = true; }}
      onMouseLeave={() => { hovered.current = false; }}
    >
      <ol className="ext-demo-steps">
        {panels.map((item, idx) => (
          <li key={item.title}>
            <button
              type="button"
              className={`ext-demo-step${step === idx ? ' is-on' : ''}`}
              onClick={() => setStep(idx)}
            >
              <span className="ext-demo-num">{idx + 1}</span>
              <span>
                <strong>{item.title}</strong>
                <em>{item.body}</em>
              </span>
            </button>
          </li>
        ))}
      </ol>
      <div className="chrome-mock" aria-hidden="true">
        <div className="chrome-bar">
          <span /><span /><span />
          <div className="chrome-url">accountant.nl / ifrs-update</div>
        </div>
        <div className={`chrome-body is-${panels[step].mock}`}>
          {panels[step].mock === 'store' && (
            <div className="mock-store">
              <p>CPD Pro — Article Quiz</p>
              <span>{t('cpdLanding.extDownload.cta')}</span>
            </div>
          )}
          {panels[step].mock === 'panel' && (
            <div className="mock-panel">
              <p>CPD Pro</p>
              <div className="mock-field" />
              <div className="mock-field short" />
              <span>{t('cpdLanding.hero.ctaSignIn')}</span>
            </div>
          )}
          {panels[step].mock === 'extract' && (
            <div className="mock-extract">
              <div className="mock-article" />
              <div className="mock-article short" />
              <span>Extract Article</span>
            </div>
          )}
          {panels[step].mock === 'earn' && (
            <div className="mock-earn">
              <p>{t('cpdLanding.hero.certKicker')}</p>
              <strong>1.0 CPD</strong>
              <span>CPD-8F2A-79C4</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CpdLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [counts, setCounts] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [statsPlay, setStatsPlay] = useState(false);
  const [billing, setBilling] = useState('annual');
  const [whyFocus, setWhyFocus] = useState(0);
  const [howPlat, setHowPlat] = useState(0);
  const [howExt, setHowExt] = useState(0);
  const statsRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    fetch(`${API_URL}/public-content-counts`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { if (isMounted) setCounts(data); })
      .catch(() => { /* silent fail for UI */ });
    return () => { isMounted = false; };
  }, []);

  const path = location?.pathname || '/';
  const isHome = path === '/' || path === '/home';

  useEffect(() => {
    if (!isHome) return undefined;
    const node = statsRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsPlay(true); },
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [isHome]);

  const articlesCount = counts?.articles ?? 59;
  const youtubeCount = counts?.youtube_videos ?? 10;
  const isAbonnementen = path === '/abonnementen';
  const isWaarom = path === '/waarom-cpd-pro';
  const isAccreditatie = path === '/accreditatie';
  const isFaq = path === '/faq';
  const isHoeWerkt = path === '/hoe-werkt-cpd-pro';

  const faqItems = t('cpdLanding.faq.items', { returnObjects: true });
  const safeFaqItems = Array.isArray(faqItems) ? faqItems : [];
  const goRegister = () => navigate(`/register?lang=${i18n.language}`);

  return (
    <div className={`cpd-landing-page is-branded${isHome ? ' is-home' : ''}`}>

      {isHome && (
        <section className="home-hero">
          <div className="container wide home-hero-grid">
            <div className="home-hero-copy">
              <p className="hero-eyebrow">{t('cpdLanding.hero.eyebrow')}</p>
              <h1 className="cpd-hero-title">{t('cpdLanding.hero.title')}</h1>
              <p className="cpd-hero-subtitle">{t('cpdLanding.hero.subtitle1')}</p>
              <div className="hero-cta">
                <button className="btn-primary-cta" type="button" onClick={goRegister}>
                  {t('cpdLanding.hero.ctaStart')}
                </button>
                <button className="btn-secondary-cta" type="button" onClick={() => navigate('/login')}>
                  {t('cpdLanding.hero.ctaSignIn')}
                </button>
              </div>
              <div className="hero-trust">
                <span>{t('cpdLanding.hero.trust1')}</span>
                <span>{t('cpdLanding.hero.trust2')}</span>
                <span>{t('cpdLanding.hero.trust3')}</span>
              </div>
            </div>
            <HeroStage t={t} />
          </div>
        </section>
      )}

      {isHome && (
        <>
          <Reveal as="section" className="home-domains">
            <div className="container wide">
              <p className="section-kicker">{t('cpdLanding.proContext.lead3')}</p>
              <div className="domain-row">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="domain-chip"
                    onClick={() => navigate('/waarom-cpd-pro')}
                  >
                    {t(`cpdLanding.proContext.pill${n}`)}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-proof">
            <div className="container wide home-proof-grid" ref={statsRef}>
              <div className="proof-copy">
                <h2>{t('cpdLanding.proContext.title')}</h2>
                <p>{t('cpdLanding.proContext.lead1')}</p>
                <p>{t('cpdLanding.proContext.sublead1')}</p>
                <button className="link-btn" type="button" onClick={() => navigate('/accreditatie')}>
                  {t('cpdLanding.proContext.accreditationLink')}
                </button>
              </div>
              <div className="proof-stats">
                <div className="proof-stat">
                  <strong><CountUp value={articlesCount} play={statsPlay} /></strong>
                  <span>{t('cpdLanding.expect.articles')}</span>
                </div>
                <div className="proof-stat">
                  <strong><CountUp value={youtubeCount} play={statsPlay} /></strong>
                  <span>{t('cpdLanding.expect.youtube')}</span>
                </div>
                <div className="proof-note">{t('cpdLanding.expect.subtitle')}</div>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-why">
            <div className="container wide">
              <div className="section-head">
                <h2>{t('cpdLanding.whyChoose.title')}</h2>
                <button className="link-btn" type="button" onClick={() => navigate('/waarom-cpd-pro')}>
                  {t('cpdLanding.whyChoose.link')}
                </button>
              </div>
              <div className="why-mosaic">
                {[
                  { n: 1, icon: 'shield' },
                  { n: 2, icon: 'case' },
                  { n: 3, icon: 'layers' },
                  { n: 4, icon: 'bolt' },
                ].map((card) => (
                  <article key={card.n} className={`why-tile why-tile-${card.n}`}>
                    <div className="why-icon" aria-hidden="true">
                      {card.icon === 'shield' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>
                      )}
                      {card.icon === 'case' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
                      )}
                      {card.icon === 'layers' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 2 9 5-9 5-9-5 9-5z" /><path d="m3 12 9 5 9-5" /></svg>
                      )}
                      {card.icon === 'bolt' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                      )}
                    </div>
                    <h3>{t(`cpdLanding.whyChoose.card${card.n}Title`)}</h3>
                    <p>{t(`cpdLanding.whyChoose.card${card.n}Desc`)}</p>
                  </article>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-how">
            <div className="container wide">
              <div className="section-head">
                <h2>{t('cpdLanding.howWorks.title')}</h2>
              </div>
              <HowInteractive t={t} navigate={navigate} />
            </div>
          </Reveal>

          <Reveal as="section" className="home-ext">
            <div className="container wide">
              <div className="ext-head">
                <span className="hero-eyebrow">{t('cpdLanding.extDownload.badge')}</span>
                <h2>{t('cpdLanding.extDownload.title')}</h2>
                <p>{t('cpdLanding.extDownload.sub')}</p>
              </div>
              <ExtensionDemo t={t} />
              <div className="ext-cta-row">
                <a className="ext-download-btn" href={CHROME_EXT_URL} target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
                  </svg>
                  {t('cpdLanding.extDownload.cta')}
                </a>
                <p className="ext-download-note">{t('cpdLanding.extDownload.note')}</p>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-pricing">
            <div className="container wide">
              <div className="section-head">
                <h2>{t('cpdLanding.pricingPreview.title')}</h2>
                <button className="link-btn" type="button" onClick={() => navigate('/abonnementen')}>
                  {t('cpdLanding.pricingPreview.link')}
                </button>
              </div>
              <div className="bill-toggle" role="group" aria-label={t('cpdLanding.pricingPreview.title')}>
                <button
                  type="button"
                  className={billing === 'monthly' ? 'is-on' : ''}
                  onClick={() => setBilling('monthly')}
                >
                  {t('cpdLanding.pricingPreview.toggleMonthly')}
                </button>
                <button
                  type="button"
                  className={billing === 'annual' ? 'is-on' : ''}
                  onClick={() => setBilling('annual')}
                >
                  {t('cpdLanding.pricingPreview.toggleAnnual')}
                  <em>{t('cpdLanding.pricingPreview.saveBadge')}</em>
                </button>
              </div>
              <div className={`price-stage is-${billing}`}>
                <article className={`price-card${billing === 'monthly' ? ' is-active' : ''}`}>
                  <h3>{t('cpdLanding.pricingPreview.monthlyTitle')}</h3>
                  <p className="price-amount">{t('cpdLanding.pricingPreview.monthlyPrice')}</p>
                  <p>{t('cpdLanding.pricingPreview.monthlyDesc')}</p>
                  <ul>
                    <li>{t('cpdLanding.subscriptions.featureMonthlyAllowance')}</li>
                    <li>{t('cpdLanding.subscriptions.featureCertificates')}</li>
                    <li>{t('cpdLanding.subscriptions.featureAuditReady')}</li>
                    <li>{t('cpdLanding.subscriptions.featureExtension')}</li>
                  </ul>
                  <button type="button" className="btn-primary-cta" onClick={goRegister}>
                    {t('cpdLanding.pricingPreview.cta')}
                  </button>
                </article>
                <article className={`price-card featured${billing === 'annual' ? ' is-active' : ''}`}>
                  <span className="price-popular">{t('lp.mostPopular', 'Most Popular')}</span>
                  <h3>{t('cpdLanding.pricingPreview.annualTitle')}</h3>
                  <p className="price-amount">{t('cpdLanding.pricingPreview.annualPrice')}</p>
                  <p>{t('cpdLanding.pricingPreview.annualDesc')}</p>
                  <ul>
                    <li>{t('cpdLanding.subscriptions.featureCertificates')}</li>
                    <li>{t('cpdLanding.subscriptions.featureAuditReady')}</li>
                    <li>{t('cpdLanding.subscriptions.featureExtension')}</li>
                  </ul>
                  <button type="button" className="btn-primary-cta" onClick={goRegister}>
                    {t('cpdLanding.pricingPreview.cta')}
                  </button>
                </article>
              </div>
            </div>
          </Reveal>
        </>
      )}

      {isWaarom && (
        <>
          <section className="page-hero">
            <div className="container wide">
              <h1>{t('cpdLanding.whyPage.title')}</h1>
              <p>{t('cpdLanding.whyPage.lead')}</p>
              <div className="risk-problems">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`risk-problem${whyFocus === n - 1 ? ' is-on' : ''}`}
                    onClick={() => setWhyFocus(n - 1)}
                  >
                    {t(`cpdLanding.whyPage.problem${n}`)}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <Reveal as="section" className="risk-philosophy brand-section">
            <div className="container">
              <h2 className="section-title">{t('cpdLanding.whyPage.philosophyTitle')}</h2>
              <div className="philosophy-card">
                <p className="philosophy-quote">
                  {t('cpdLanding.whyPage.philosophyQuote')}
                </p>
                <p className="philosophy-sub">
                  {t('cpdLanding.whyPage.philosophySub')}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="risk-benefits brand-section">
            <div className="container">
              <h2 className="section-title">{t('cpdLanding.whyPage.benefitsTitle')}</h2>
              <div className="because-grid">
                {[1, 2, 3].map((n) => (
                  <article key={n} className={`because-card${whyFocus === n - 1 ? ' is-on' : ''}`}>
                    <div className="because-icon">
                      {n === 1 && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>
                      )}
                      {n === 2 && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                      )}
                      {n === 3 && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                      )}
                    </div>
                    <div className="because-title">{t(`cpdLanding.whyPage.benefit${n}Title`)}</div>
                    <div className="because-desc">{t(`cpdLanding.whyPage.benefit${n}Desc`)}</div>
                  </article>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="risk-compare brand-section">
            <div className="container">
              <h2 className="section-title">{t('cpdLanding.whyPage.compareTitle')}</h2>
              <CompareDuel />
            </div>
          </Reveal>
        </>
      )}

      {isAbonnementen && (
        <>
          <section className="page-hero">
            <div className="container wide">
              <h1>{t('cpdLanding.subscriptions.title')}</h1>
              <p>{t('cpdLanding.subscriptions.lead')}</p>
            </div>
          </section>

          <Reveal as="section" id="pricing" className="home-pricing brand-section">
            <div className="container wide">
              <div className="bill-toggle" role="group" aria-label={t('cpdLanding.pricingPreview.title')}>
                <button type="button" className={billing === 'monthly' ? 'is-on' : ''} onClick={() => setBilling('monthly')}>
                  {t('cpdLanding.pricingPreview.toggleMonthly')}
                </button>
                <button type="button" className={billing === 'annual' ? 'is-on' : ''} onClick={() => setBilling('annual')}>
                  {t('cpdLanding.pricingPreview.toggleAnnual')}
                  <em>{t('cpdLanding.pricingPreview.saveBadge')}</em>
                </button>
              </div>
              <div className={`price-stage is-${billing}`}>
                <article className={`price-card${billing === 'monthly' ? ' is-active' : ''}`}>
                  <h3>{t('lp.pricing.monthly.title','Professional Monthly')}</h3>
                  <p className="price-amount">€19.99</p>
                  <p>{t('cpdLanding.subscriptions.monthlyDesc')}</p>
                  <ul>
                    <li>{t('cpdLanding.subscriptions.featureMonthlyAllowance')}</li>
                    <li>{t('cpdLanding.subscriptions.featureReadArticles')}</li>
                    <li>{t('cpdLanding.subscriptions.featureCertificates')}</li>
                    <li>{t('cpdLanding.subscriptions.featureAuditReady')}</li>
                    <li>{t('cpdLanding.subscriptions.featureExtension')}</li>
                  </ul>
                  <button type="button" className="btn-primary-cta" onClick={goRegister}>{t('cpdLanding.subscriptions.ctaEliminate')}</button>
                </article>
                <article className={`price-card featured${billing === 'annual' ? ' is-active' : ''}`}>
                  <span className="price-popular">{t('lp.mostPopular','Most Popular')}</span>
                  <h3>{t('lp.pricing.pro.title','Professional')}</h3>
                  <p className="price-amount">€199.99</p>
                  <p>{t('cpdLanding.subscriptions.proDesc')}</p>
                  <ul>
                    <li>{t('cpdLanding.subscriptions.featureReadArticles')}</li>
                    <li>{t('cpdLanding.subscriptions.featureCertificates')}</li>
                    <li>{t('cpdLanding.subscriptions.featureAuditReady')}</li>
                    <li>{t('cpdLanding.subscriptions.featureExtension')}</li>
                  </ul>
                  <p className="plan-subnote">{t('cpdLanding.subscriptions.proSubnote')}</p>
                  <button type="button" className="btn-primary-cta" onClick={goRegister}>{t('cpdLanding.subscriptions.ctaEliminate')}</button>
                </article>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-calc brand-section">
            <div className="container wide">
              <div className="section-head calc-head">
                <div>
                  <p className="hero-eyebrow">{t('cpdLanding.calc.kicker')}</p>
                  <h2 className="section-title">{t('cpdLanding.calc.title')}</h2>
                  <p className="calc-lead">{t('cpdLanding.calc.lead')}</p>
                </div>
              </div>
              <CostCalculator onRegister={goRegister} />
            </div>
          </Reveal>

          <Reveal as="section" className="home-cycle">
            <div className="container wide">
              <div className="cycle-copy">
                <p className="hero-eyebrow">{t('cpdLanding.yearCycle.kicker')}</p>
                <h2>{t('cpdLanding.yearCycle.title')}</h2>
                <p>{t('cpdLanding.yearCycle.lead')}</p>
              </div>
              <YearCycle />
            </div>
          </Reveal>

          <Reveal as="section" className="included brand-section">
            <div className="container">
              <h2 className="section-title">{t('cpdLanding.subscriptions.includedTitle')}</h2>
              <p className="included-subtitle">{t('cpdLanding.subscriptions.includedSubtitle')}</p>
              <div className="included-grid">
                {[
                  ['featureRecognisedUnits', 'featureRecognisedUnitsDesc'],
                  ['featureCertificates', 'featureCertificatesDesc'],
                  ['featureLogHistory', 'featureLogHistoryDesc'],
                  ['featureAuditReady', 'featureAuditReadyDesc'],
                ].map(([title, desc]) => (
                  <div className="included-item" key={title}>
                    <div className="included-title">{t(`cpdLanding.subscriptions.${title}`)}</div>
                    <div className="included-desc">{t(`cpdLanding.subscriptions.${desc}`)}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </>
      )}

      {isAccreditatie && (
        <>
          <section className="page-hero">
            <div className="container wide">
              <h1>{t('cpdLanding.accreditation.title')}</h1>
              <p>{t('cpdLanding.accreditation.subtitle')}</p>
            </div>
          </section>
          <Reveal as="section" id="accreditatie" className="accredited-section brand-section">
            <div className="container accredited-section-inner">
              <div className="accredited-card">
                <div className="accredited-card-left">
                  <div className="accredited-text">NiRV</div>
                  <a href="https://www.nirv.nl" target="_blank" rel="noopener noreferrer" className="accredited-logo-link">
                    <img src="/nirv-logo.png" alt="NiRV" className="accredited-logo" />
                  </a>
                </div>
                <ul className="accredited-bullets">
                  <li>{t('cpdLanding.accreditation.nirvBullet1')}</li>
                  <li>{t('cpdLanding.accreditation.nirvBullet2')}</li>
                  <li>{t('cpdLanding.accreditation.nirvBullet3')}</li>
                  <li>{t('cpdLanding.accreditation.nirvBullet4')}</li>
                </ul>
              </div>
              <div className="accredited-card">
                <div className="accredited-card-left">
                  <div className="accredited-text">{t('cpdLanding.accreditation.nbaTitle')}</div>
                </div>
                <ul className="accredited-bullets">
                  <li>{t('cpdLanding.accreditation.nbaBullet1')}</li>
                  <li>{t('cpdLanding.accreditation.nbaBullet2')}</li>
                  <li>{t('cpdLanding.accreditation.nbaBullet3')}</li>
                  <li>{t('cpdLanding.accreditation.nbaBullet4')}</li>
                </ul>
              </div>
              <div className="accredited-partner">
                <h3 className="accredited-partner-title">{t('cpdLanding.accreditation.partnerTitle')}</h3>
                <p className="accredited-partner-text">{t('cpdLanding.accreditation.partnerText')}</p>
                <a className="accredited-partner-cta" href="mailto:service@protagon.ai?subject=Accreditation%20enquiry">{t('cpdLanding.accreditation.partnerCta')}</a>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="home-verify">
            <div className="container wide">
              <div className="verify-copy">
                <p className="hero-eyebrow">{t('cpdLanding.verify.kicker')}</p>
                <h2>{t('cpdLanding.verify.title')}</h2>
                <p>{t('cpdLanding.verify.lead')}</p>
              </div>
              <CertificateVerifier />
            </div>
          </Reveal>
        </>
      )}

      {isHoeWerkt && (
        <>
          <section className="page-hero">
            <div className="container wide">
              <h1>{t('cpdLanding.howItWorks.title')}</h1>
              <p>{t('cpdLanding.howItWorks.lead')}</p>
            </div>
          </section>

          <Reveal as="section" className="brand-section how-page">
            <div className="container wide">
              <h2 className="section-title align-left">{t('cpdLanding.howItWorks.platformTitle')}</h2>
              <div className="how-page-grid">
                <div className="how-rail" role="tablist">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="tab"
                      className={`how-rail-btn${howPlat === n - 1 ? ' is-on' : ''}`}
                      onClick={() => setHowPlat(n - 1)}
                    >
                      <span className="how-rail-num">{n}</span>
                      <span className="how-rail-title">{t(`cpdLanding.howItWorks.step${n}Title`)}</span>
                    </button>
                  ))}
                </div>
                <div className="how-panel">
                  <p className="how-panel-kicker">{t('cpdLanding.howItWorks.platformTitle')}</p>
                  <h3>{t(`cpdLanding.howItWorks.step${howPlat + 1}Title`)}</h3>
                  <p>{t(`cpdLanding.howItWorks.step${howPlat + 1}Desc`)}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal as="section" className="brand-section how-page">
            <div className="container wide">
              <h2 className="section-title align-left">{t('cpdLanding.howItWorks.extensionTitle')}</h2>
              <p className="included-subtitle" style={{ textAlign: 'left', marginLeft: 0 }}>{t('cpdLanding.howItWorks.extensionLead')}</p>
              <div className="how-page-grid">
                <div className="how-rail" role="tablist">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="tab"
                      className={`how-rail-btn${howExt === n - 1 ? ' is-on' : ''}`}
                      onClick={() => setHowExt(n - 1)}
                    >
                      <span className="how-rail-num">{n}</span>
                      <span className="how-rail-title">{t(`cpdLanding.howItWorks.extStep${n}Title`)}</span>
                    </button>
                  ))}
                </div>
                <div className="how-panel">
                  <p className="how-panel-kicker">{t('cpdLanding.extDownload.badge')}</p>
                  <h3>{t(`cpdLanding.howItWorks.extStep${howExt + 1}Title`)}</h3>
                  <p>{t(`cpdLanding.howItWorks.extStep${howExt + 1}Desc`)}</p>
                  <a className="ext-download-btn" href={CHROME_EXT_URL} target="_blank" rel="noopener noreferrer">
                    {t('cpdLanding.howItWorks.extensionCta')}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </>
      )}

      {isFaq && (
        <>
          <section className="page-hero">
            <div className="container wide">
              <h1>{t('cpdLanding.faq.title')}</h1>
            </div>
          </section>
          <Reveal as="section" id="faq" className="cpd-faq brand-section">
            <div className="container">
              <div className="faq-list">
                {safeFaqItems.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div key={item.q || index} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-question"
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.q}</span>
                        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
                      </button>
                      {isOpen && (
                        <div className="faq-answer">
                          <p>{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </>
      )}

      <footer className="cpd-footer home-footer">
        <div className="container wide home-footer-grid">
          <div>
            <p className="home-footer-brand">CPD Pro</p>
            <p>{t('cpdLanding.footerTagline')}</p>
          </div>
          <div className="home-footer-links">
            <button type="button" onClick={() => navigate('/abonnementen')}>{t('landingNav.subscriptions', 'Subscriptions')}</button>
            <button type="button" onClick={() => navigate('/waarom-cpd-pro')}>{t('landingNav.why', 'Why CPD Pro?')}</button>
            <button type="button" onClick={() => navigate('/hoe-werkt-cpd-pro')}>{t('landingNav.howItWorks', 'How it works')}</button>
            <button type="button" onClick={() => navigate('/accreditatie')}>{t('landingNav.accreditation', 'Accreditation')}</button>
          </div>
          <div className="home-footer-links">
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy', 'Privacy Policy')}</a>
            <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">{t('auth.termsOfService', 'Terms of Service')}</a>
            <a href="https://protagon.ai" target="_blank" rel="noopener noreferrer">{t('cpdLanding.footerProtagon', 'Visit Protagon')}</a>
            <a href="mailto:service@protagon.ai">service@protagon.ai</a>
          </div>
        </div>
        <p className="home-footer-copy">{t('cpdLanding.footer')}</p>
      </footer>
    </div>
  );
};

export default CpdLandingPage;
